import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import {
  CatalogWishlists,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogWishlists/CatalogWishlists';
import { PlaceBid } from 'src/components/PlaceBid/PlaceBid';
import {
  CatalogTextAlerts,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogTextAlerts/CatalogTextAlerts';
import { formatCurrency } from 'src/lib/catalog';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import { BiddingContext } from 'src/lib/BiddingContext';
import { Button, cn, Typography } from '@riac/design-system';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { getEndMessage } from 'src/components/AuctionInfo/TimedAuctionInfo';
import { countIncrements } from '@riac/core';

interface IAuctionItemActions {
  item: IAuctionItem;
  compact: boolean;
}

export const getBidCountMessage = (start, end) => {
  const increments = countIncrements(start, end);
  let label = 'bid';
  if(increments === 0) {
    return '';
  }
  if(increments > 1) {
    label += 's';
  }
  return `(${increments} ${label})`;
};

export const AuctionItemActions = (props: IAuctionItemActions): JSX.Element => {
  // DayJS Note: Interpret times in America/Chicago timezone
  const endDate = dayjs.tz(props.item.EndDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  const { auctionTypeId, auctionTitle, compactMode } = useContext(CatalogContext);
  const [bidInCart, setBidInCart] = useState(false);
  const [timedAuctionLotActive, setTimedAuctionLotActive] = useState(endDate.isAfter(now));
  const { getMessageForLot } = useContext(BiddingContext);

  useEffect(() => {
    const e = dayjs.tz(props.item.EndDate, 'America/Chicago');
    const n = dayjs().tz('America/Chicago');
    const diff = e.diff(n);
    let timer = null;
    if(e.isAfter(n) && diff > 0 && diff < 86400000) {
      setTimedAuctionLotActive(true);
      timer = setTimeout(() => {
        setTimedAuctionLotActive(false);
      }, diff+1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [props.item.EndDate]);

  return (
    <div className='flex items-stretch justify-stretch w-full border-border-dark md:border-l-1 md:pl-8'>
      <LoginToUse
        overlayText='Log In to Place Bids or Create Wishlists and Text Alerts'
        childrenWrapperClassName={cn(
          'flex-col gap-3 flex flex-1',
        )}
        className='flex-1 flex'
      >
        {auctionTypeId === TIMED_AUCTION_TYPE_ID
          && <div className={cn('flex flex-col md:flex-row gap-3 items-stretch justify-stretch flex-wrap w-full', compactMode && 'md:items-start items-stretch')}>
            <div className='flex flex-1 flex-col min-w-32 gap-1'>
              <Typography color='text-lighter' className='text-base leading-6' weight='bold'>
                {props.item.CurrentBid && <>Current Bid</> || <>Starting Bid</>}
              </Typography>
              <Typography color='text-light' weight='bold' className='text-lg leading-[22px]'>
                {props.item.CurrentBid && <div className={'flex flex-row gap-2'}>
                  {props.item.CurrentBid ? formatCurrency(props.item.CurrentBid) : 'No bids'}
                  <Typography size={'sm'} weight={'regular'}>{getBidCountMessage(props.item.StartingBid, props.item.CurrentBid)}</Typography>
                </div> || <>
                  {formatCurrency(props.item.StartingBid)}
                </>}
              </Typography>
            </div>
            {(props.item.HighBidder && (
              <div className='flex flex-col gap-1 w-full'>
                <Typography color='text-light' size='sm'>Your max bid is <span className='font-medium'>{formatCurrency(props.item.HighBidAmount)}</span></Typography>
                <Typography size='sm' className={'flex-1 flex w-full text-success-darker bg-success-lighter p-1 px-2 border-success-light border-1'}>
                  You are the high bidder
                </Typography>
              </div>
            )) || (props.item.PreviousBid
                && <div className='flex flex-col gap-1 w-full'>
                  <Typography size='sm' className={'flex-1 flex w-full text-error-darker bg-error-lighter p-1 px-2 border-error-light border-1'}>
                    You are out bid
                  </Typography>
                </div>
                || '')}
          </div>
        || <div>
          {TIMED_AUCTION_TYPE_ID !== auctionTypeId && <div className='flex flex-1 flex-col min-w-32 gap-1'>
            <Typography color='text-lighter' weight='medium'>
              Estimated Price
            </Typography>
            <Typography color='text-light' weight='bold' size='lg'>
              {props.item.CurrentBid && <>
                {props.item.CurrentBid ? formatCurrency(props.item.CurrentBid) : 'No bids'}
              </> || <>
                {
                  getIsPricelessLot(props?.item?.AuctionID, props?.item?.LotNumber) ? 'Priceless'
                    : (
                      (props?.item?.LowEst && props?.item?.HighEst)
                        ? `${formatCurrency(props.item.LowEst)} - ${formatCurrency(props.item.HighEst)}`
                        : 'Not Available'
                    )
                }
              </>}
            </Typography>
          </div>}
        </div>}
        <div className='flex flex-col gap-3 justify-between flex-1'>
          {getMessageForLot(props.item.LotNumber) || <div className='hidden md:block'/>}
          <div className='flex flex-col gap-3'>
            {timedAuctionLotActive || auctionTypeId !== TIMED_AUCTION_TYPE_ID ? (
              <div className='flex flex-col xl:flex-row gap-3 flex-wrap justify-items-stretch'>
                <PlaceBid
                  type='sealed'
                  auctionTypeID={auctionTypeId}
                  item={props.item}
                  addBid={() =>{
                    if(auctionTypeId !== TIMED_AUCTION_TYPE_ID) {
                      setBidInCart(true);
                    }
                  }}
                  className='flex-1 w-full'
                />
                {(auctionTypeId === TIMED_AUCTION_TYPE_ID) && (
                  <PlaceBid
                    type={'increment'}
                    item={props.item}
                    auctionTypeID={auctionTypeId}
                    variant='secondary'
                    addBid={() => {
                    }}
                    className='flex-1 w-full'
                  />
                )}
                {(auctionTypeId !== TIMED_AUCTION_TYPE_ID) && (
                  <PlaceBid
                    type='phone'
                    auctionTypeID={auctionTypeId}
                    item={props.item}
                    variant='secondary'
                    addBid={() => setBidInCart(true)}
                    className='flex-1 w-full'
                  />
                ) || ''}
              </div>
            ) : ''}
            {(props.item.citem === 1 || bidInCart) && (
              <div>
                <Button className='p-0' variant='link' to='/account/cart' title='View Your Bid Cart'>
                  In cart
                </Button>
              </div>
            )}
            {auctionTypeId !== TIMED_AUCTION_TYPE_ID && props.item.sbitem === 1 && (
              <div>
                <Button className='p-0' variant='link' to='/account/bids' title='View All Submitted Bids'>
                  Bid submitted
                </Button>
              </div>
            )}
            <div className='flex flex-col justify-start items-start gap-1'>
              <CatalogWishlists
                item={props.item}
                title={auctionTitle}
                inverted
                noOptionsBorder
              />
              {auctionTypeId !== TIMED_AUCTION_TYPE_ID ? <CatalogTextAlerts item={props.item}/> : null}
            </div>
          </div>
        </div>
      </LoginToUse>
    </div>
  );
};
